import { useCallback, useEffect, useState, useMemo } from 'react';
import { getMunicipalitiesListAction } from 'features/municipalities/redux/actions';
import { useAppDispatch } from 'shared/services/redux/hooks/use-dispatch';
import { useAppSelector } from 'shared/services/redux/hooks/use-selector';
import {
  getDataIndexedColumns,
  getDefaultTablePagination,
  useTableSearchParams,
} from 'shared/UI/UIKit/Table';
import { useFilteredColumns } from 'common/ColumnsFilter/use-filtered-columns';
import type { AppTableColumn, AppTableState } from 'shared/UI/UIKit/Table';
import type { MunicipalityWithRegion, MunicipalitiesListRequest } from 'services/api';
import type { TableFilterField } from 'common/TableFilter';
import type { AppSelectOption } from 'shared/UI/UIKit/Select';
import { getRegionOptions } from '../Form/Municipality/use-regions';
import { clearState, setSelectedMunicipality } from '../../redux/slice';
import { getMunicipalitiesTableColumns } from './columns';
import { municipalitiesFilterFields } from './filter-fields';

const getInitialStateWithSorters = (
  columns: AppTableColumn<MunicipalityWithRegion>[],
): AppTableState => {
  return {
    sorters: getDataIndexedColumns(columns)
      .filter(({ key }) => key !== 'controls')
      .map(({ key, dataIndex }) => {
        return key === 'name'
          ? {
              key,
              dataIndex,
              order: 'ascend',
            }
          : {
              key,
              dataIndex,
            };
      }),
    filters: [],
    pagination: getDefaultTablePagination(),
  };
};

export const useMunicipalities = () => {
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [removeModalVisible, setRemoveModalVisible] = useState(false);
  const dispatch = useAppDispatch();
  const { municipalities, total, loading } = useAppSelector((state) => state.municipalitiesState);
  const [siderVisible, setSiderVisible] = useState(false);
  const [filterFields, setFilterFields] = useState<TableFilterField[]>(municipalitiesFilterFields);

  const editMunicipality = useCallback(
    (id: number) => {
      const selectedMunicipality = municipalities.find((municipality) => municipality.id === id);

      if (selectedMunicipality) {
        dispatch(setSelectedMunicipality(selectedMunicipality));
        setEditModalVisible(true);
      }
    },
    [municipalities, dispatch],
  );

  const removeMunicipality = useCallback(
    (id: number) => {
      const selectedMunicipality = municipalities.find((municipality) => municipality.id === id);

      if (selectedMunicipality) {
        dispatch(setSelectedMunicipality(selectedMunicipality));
        setRemoveModalVisible(true);
      }
    },
    [municipalities, dispatch],
  );

  const columns = useMemo(
    () => getMunicipalitiesTableColumns(editMunicipality, removeMunicipality),
    [editMunicipality, removeMunicipality],
  );
  const { filteredColumns, hiddenColumns, visibleKeys, setVisibleKeys } =
    useFilteredColumns(columns);

  const getTableItems = useCallback(
    (nextTableState: AppTableState, nextFilterState: Record<string, any>) => {
      const {
        pagination: { current, pageSize },
        sorters,
      } = nextTableState;
      const sort = sorters
        .filter((col) => !!col.order)
        .map((col) => `${col.dataIndex.join('.')},${col.order === 'ascend' ? 'asc' : 'desc'}`);

      const apiParams: MunicipalitiesListRequest = {
        page: current! - 1,
        size: pageSize,
        sort: sort.length > 0 ? sort : undefined,
        ...nextFilterState,
      };

      dispatch(getMunicipalitiesListAction(apiParams));
    },
    [dispatch],
  );

  const initialState = useMemo(() => getInitialStateWithSorters(columns), [columns]);

  const { tableState, onTableChange, filterState, filterCount, onFilterChange, onItemRemove } =
    useTableSearchParams(initialState, getTableItems, municipalities.length, total, hiddenColumns);

  useEffect(() => {
    return () => {
      dispatch(clearState());
    };
  }, [dispatch]);

  const createMunicipality = useCallback(() => {
    setCreateModalVisible(true);
  }, []);

  const showSider = useCallback(() => {
    setSiderVisible(true);
  }, []);

  const hideSider = useCallback(() => {
    setSiderVisible(false);
  }, []);

  const onLoadFilterOptions = async (fieldName: string) => {
    const filterFieldsCopy = [...filterFields];
    const loadingIndex = filterFieldsCopy.findIndex((field) => field.name === fieldName);
    if (loadingIndex < 0) return;

    filterFieldsCopy[loadingIndex].loading = true;
    setFilterFields([...filterFieldsCopy]);

    let options: AppSelectOption[] = [];
    switch (fieldName) {
      case 'regionId':
        options = await getRegionOptions();
        break;
      default:
        return;
    }

    filterFieldsCopy[loadingIndex].loading = false;
    filterFieldsCopy[loadingIndex].options = options;
    setFilterFields([...filterFieldsCopy]);
  };

  const hideEditModal = () => {
    setEditModalVisible(false);
    dispatch(setSelectedMunicipality(null));
  };

  return {
    state: {
      loading,
      municipalities,
      removeModalVisible,
      createModalVisible,
      editModalVisible,
      tableState,
      siderVisible,
      columns,
      filteredColumns,
      visibleKeys,
      filterState,
      filterCount,
      filterFields,
    },
    methods: {
      setRemoveModalVisible,
      createMunicipality,
      editMunicipality,
      removeMunicipality,
      setCreateModalVisible,
      hideEditModal,
      onTableChange,
      showSider,
      hideSider,
      setVisibleKeys,
      onFilterChange,
      onItemRemove,
      onLoadFilterOptions,
    },
  };
};
